import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

export const useListPagination = (items, resetDeps = []) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const totalCount = items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, ...resetDeps]);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const paginatedItems = useMemo(
    () => items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [items, page, rowsPerPage]
  );

  return { page, setPage, rowsPerPage, setRowsPerPage, totalCount, totalPages, paginatedItems };
};

const ListPagination = ({
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  itemLabel = 'item',
  itemLabelPlural,
}) => {
  const plural = itemLabelPlural || `${itemLabel}s`;
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const rangeStart = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const rangeEnd = Math.min((page + 1) * rowsPerPage, totalCount);
  const emptyLabel = `No ${plural} to show`;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        px: 2,
        py: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {totalCount === 0 ? (
            emptyLabel
          ) : (
            <>
              Showing{' '}
              <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {rangeStart}–{rangeEnd}
              </Typography>{' '}
              of{' '}
              <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {totalCount}
              </Typography>
            </>
          )}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 72 }}>
          <Select
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
            sx={{
              height: 28,
              fontSize: '0.75rem',
              borderRadius: 1.25,
              '& .MuiSelect-select': { py: 0.5, px: 1.25 },
            }}
          >
            {ROWS_PER_PAGE_OPTIONS.map((n) => (
              <MenuItem key={n} value={n} sx={{ fontSize: '0.75rem' }}>
                {n} / page
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          size="small"
          disabled={page === 0 || totalCount === 0}
          onClick={() => onPageChange(page - 1)}
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.25,
            color: 'text.secondary',
            '&.Mui-disabled': { color: 'text.disabled' },
            '&:hover:not(.Mui-disabled)': { bgcolor: 'action.hover' },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 72, textAlign: 'center' }}>
          Page{' '}
          <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {totalCount === 0 ? 0 : page + 1}
          </Typography>{' '}
          of {totalCount === 0 ? 0 : totalPages}
        </Typography>
        <IconButton
          size="small"
          disabled={page >= totalPages - 1 || totalCount === 0}
          onClick={() => onPageChange(page + 1)}
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.25,
            color: 'text.secondary',
            '&.Mui-disabled': { color: 'text.disabled' },
            '&:hover:not(.Mui-disabled)': { bgcolor: 'action.hover' },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ListPagination;
